import { useEffect, useRef } from "react";
import Footer from "@/components/Footer";

const platformFeatures = [
  {
  title: "Onboarding Assistant",
  desc: "Cuts onboarding time by guiding every step, chasing missing items, and keeping records complete and audit-ready.",
  image: "/assets/onboarding-assistant.svg",
  span: 1,

  // NEW (tile-by-tile control)
  mediaFit: "cover",
copyBasis: "40%", // NEW
imgStyle: {
  right: "-10%",
  bottom: "-16%",
  height: "150%",
  width: "auto",
  transform: "scale(1.02)",
}
},
 {
  title: "Meeting Notes",
  desc: "Focus on relationship building while Layla captures meeting notes, updates your CRM and flags what's missing.",
  image: "/assets/meeting-notes.svg",
  span: 1,
  mediaFit: "contain",
  imgStyle: {
    right: "6%",                // shift LEFT (less off the right edge)
    bottom: "-12%",             // keep it sitting lower
    width: "clamp(240px, 78%, 380px)", // bigger
    height: "125%",             // bigger
    objectPosition: "center bottom",
    transform: "scale(1.02)",
    transformOrigin: "bottom right",
  },
},
  {
    title: "Portfolio Management",
    desc: "Unified Portfolio Dashboard - Layla consolidates every strategy and portfolio into one view, so you can track AUM, performance, and key changes instantly.",
    image: "/assets/portfolio-management.png",
    span: 2,
    // wide card: anchor bottom-right and let it spill out
    imgStyle: {
      right: "-6%",
      bottom: "-22%",
      width: "clamp(360px, 72%, 680px)",
      transform: "scale(1.02)",
    },
  },
 {
  title: "PrivateGPT",
  desc: "Our proprietary AI turns your corpus into on-demand answers. Ask any question, and Layla searches through your files and data to give an accessible, conversational, and verifiable answer.",
  image: "/assets/privategpt.png",
  span: 1,
  mediaFit: "contain",
  imgStyle: {
    left: "50%",
    top: "50%",
    width: "clamp(220px, 70%, 340px)",
    height: "auto",
    objectPosition: "center",
    transform: "translate(-50%, -50%) scale(1.02)",
    transformOrigin: "center",
  },
},
  {
  title: "Smarter Invoicing",
  desc: "Layla generates one-click invoices with automatic fee capture, approvals, and clean audit trails.",
  image: "/assets/smarter-invoicing.svg",
  span: 1,
  mediaFit: "contain",
  imgStyle: {
    right: "8%",                 // shift LEFT
    bottom: "-12%",              // lower
    width: "clamp(230px, 76%, 360px)", // bigger
    height: "120%",
    objectPosition: "center bottom",
    transform: "scale(1.02)",
    transformOrigin: "bottom right",
  },
},
  {
    title: "Market Research",
    desc: "Layla turns news & market research emails into a mandate-specific research portal.",
    image: "/assets/market-research.png",
    span: 2,
    imgStyle: {
      right: "-6%",
      bottom: "-20%",
      width: "clamp(360px, 75%, 700px)",
      transform: "scale(1.02)",
    },
  },
];

const stats = [
  {
    valueMain: "10–15 hrs",
    valueAccent: "",
    valueSuffix: "/wk",
    title: "Time Saved",
    desc: "Hours returned to advisors per week through workflow automation.",
  },
  {
    valueMain: "30–40%",
    valueAccent: "",
    valueSuffix: "uplift",
    title: "Discovery uplift",
    desc: "Improvement in lead qualification through intelligent data matching.",
  },
  {
    valueMain: "<85%",
    valueAccent: "",
    valueSuffix: "",
    title: "Error Rate",
    desc: "Reduction in manual processing errors through automated validation.",
  },
  {
    valueMain: "4–8 hrs",
    valueAccent: "",
    valueSuffix: "/week",
    title: "Compliance",
    desc: "Hours saved weekly on regulatory reporting.",
  },
];

function useSectionReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.1 }
    );
    ref.current?.querySelectorAll(".section-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  return ref;
}

function FeatureCard({ feature, index }) {
  const isWide = feature.span === 2;

  // Use per-tile copyBasis if provided; otherwise match Image 2 proportions
  const copyBasis = feature.copyBasis ?? (isWide ? "34%" : "44%");

  return (
    <article
  className={`section-reveal relative overflow-hidden rounded-2xl ${isWide ? "col-span-2" : "col-span-1"}`}
  data-testid={`platform-feature-${index}`}
  style={{
    background: "linear-gradient(180deg, #0B0B10 0%, #1E1F23 100%)",
    border: "1px solid rgba(255,255,255,0.10)",
    boxShadow: "0 18px 60px rgba(0,0,0,0.45)",
  }}
>
      {/* GLASS base (neutral/dark like How we build + Flexible deployment) */}
<div
  aria-hidden="true"
  className="absolute inset-0 pointer-events-none"
  style={{
    background:
      "radial-gradient(120% 120% at 15% 10%, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0) 55%)",
  }}
/>

{/* subtle neutral highlight (remove purple tint) */}
<div
  className="absolute inset-0 opacity-100"
  style={{
    background:
      "radial-gradient(120% 120% at 15% 10%, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.00) 55%)," +
      "radial-gradient(120% 120% at 90% 80%, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.00) 60%)," +
      "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.18) 100%)",
  }}
/>

      {/* CONTENT */}
      <div
        className={`relative z-10 flex h-full ${
          // Taller like Image 2
          isWide ? "min-h-[240px] md:min-h-[300px]" : "min-h-[220px] md:min-h-[260px]"
        }`}
      >
        {/* COPY */}
        <div className="p-5 md:p-6 shrink-0" style={{ flexBasis: copyBasis }}>
          <h3
            className="text-sm md:text-base font-semibold mb-2"
            style={{ fontFamily: "'Lora', serif", color: "#F5F5F5" }}
          >
            {feature.title}
          </h3>

          <p
            className="text-[11px] md:text-sm leading-relaxed"
            style={{ fontFamily: "'Lora', serif", color: "#A1A1AA" }}
          >
            {feature.desc}
          </p>
        </div>

        {/* MEDIA */}
        <div className="relative flex-1">
          <img
            src={feature.image}
            alt={feature.title}
            className="!absolute pointer-events-none select-none !max-w-none !max-h-none"
            style={{
              right: feature.imgStyle?.right,
              left: feature.imgStyle?.left,
              top: feature.imgStyle?.top,
              bottom: feature.imgStyle?.bottom,

              width: feature.imgStyle?.width ?? "auto",
              height: feature.imgStyle?.height ?? "115%",

              objectFit: feature.mediaFit ?? "contain",
              objectPosition: feature.imgStyle?.objectPosition ?? "right bottom",

              transform: feature.imgStyle?.transform ?? "none",
              transformOrigin: feature.imgStyle?.transformOrigin ?? "bottom right",

              filter: "drop-shadow(0 14px 30px rgba(0,0,0,0.55))",
            }}
          />
        </div>
      </div>
    </article>
  );
}

/* ===== Save Hours Stats Section (DIAGONAL layout like your design) ===== */

function StatNode({ stat, linePx = 220 }) {
  const { valueMain, valueSuffix, title, desc } = stat;

  return (
    <div className="relative pl-10" style={{ fontFamily: "'Lora', serif" }}>
      {/* dot */}
      <span
        aria-hidden="true"
        className="absolute left-0 top-0 h-3 w-3 rounded-full shadow-[0_0_18px_rgba(175,74,250,0.45)]"
        style={{
          background:
            "linear-gradient(135deg, #0013B3 0%, #6373FA 45%, #AF4AFA 100%)",
        }}
      />

      {/* vertical line (height controlled per node) */}
      <span
        aria-hidden="true"
        className="absolute left-[5px] top-4 w-px bg-white/10"
        style={{ height: `${linePx}px` }}
      />

      {/* value row */}
      <div className="flex items-baseline gap-3">
        <span className="text-3xl sm:text-4xl font-medium text-[#6373FA]">
          {valueMain}
        </span>
        {valueSuffix ? (
          <span className="text-sm text-[#AF4AFA]">{valueSuffix}</span>
        ) : null}
      </div>

      {/* title + desc */}
      <div className="mt-4">
        <div className="text-base sm:text-lg font-semibold text-[#F5F5F5]">
          {title}
        </div>
        <p className="mt-3 text-sm sm:text-base leading-relaxed text-[#A1A1AA] max-w-[28ch]">
          {desc}
        </p>
      </div>
    </div>
  );
}

function StatsSection() {

  const diagonalLayout = [
    { left: "2%",  top: "68%", line: 170 }, // 1st (lowest)
    { left: "24%", top: "55%", line: 210 }, // 2nd
    { left: "48%", top: "42%", line: 250 }, // 3rd
    { left: "72%", top: "28%", line: 290 }, // 4th (highest)
  ];

  return (
    <section
      className="relative py-24 md:py-28 px-6 md:px-12 lg:px-24 overflow-hidden"
      data-testid="platform-stats"
      style={{
        background:
          "linear-gradient(135deg, rgba(99,115,250,0.18) 0%, rgba(5,5,8,1) 55%)",
      }}
    >
      <div className="mx-auto max-w-7xl relative">
        {/* heading (smaller like your desired layout) */}
        <div className="max-w-2xl">
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05]"
            style={{ fontFamily: "'Lora', serif", color: "#F5F5F5" }}
          >
            Save hours. Serve more clients per advisor.
          </h2>

          <p
            className="mt-6 text-sm sm:text-base lg:text-lg max-w-xl leading-relaxed"
            style={{ color: "#A1A1AA", fontFamily: "'Lora', serif" }}
          >
            Layla removes the admin burden from research, reporting, and
            follow-through.
          </p>
        </div>

        {/* DESKTOP: diagonal layout */}
        <div className="relative mt-0 hidden lg:block min-h-[420px]">
          {stats.map((s, i) => {
            const pos = diagonalLayout[i] || diagonalLayout[diagonalLayout.length - 1];
            return (
              <div
                key={s.title}
                className="section-reveal absolute w-[260px]"
                style={{ left: pos.left, top: pos.top }}
              >
                <StatNode stat={s} linePx={pos.line} />
              </div>
            );
          })}
        </div>

        {/* MOBILE/TABLET: stacked (so it still looks clean) */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-10 lg:hidden">
          {stats.map((s, i) => (
            <div key={s.title} className="section-reveal">
              <StatNode stat={s} linePx={180 + i * 30} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function PlatformPage() {
  const pageRef = useSectionReveal();

  return (
    <div ref={pageRef} style={{ backgroundColor: "#050508" }}>
      {/* Hero Section */}
    <section
  className="relative pt-44 pb-72 md:pb-[22rem] px-6 md:px-12 lg:px-24 overflow-hidden"
  data-testid="platform-hero"
>
        <div className="absolute inset-0 pointer-events-none opacity-30">
          <img src="/assets/waves-bg.svg" alt="" className="w-full h-full object-cover" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h1
  className="animate-hero-title mt-10 md:mt-14 text-4xl sm:text-5xl lg:text-6xl font-medium leading-tight mb-6"
  style={{ fontFamily: "'Lora', serif", color: "#F5F5F5" }}
  data-testid="platform-headline"
>
  A modular,{" "}
  <span className="ai-gradient-text">agentic AI</span>{" "}
  platform that connects to your data and runs your workflows across the firm securely.
</h1>

          <p
            className="animate-hero-sub text-sm md:text-base max-w-2xl mx-auto"
            style={{ color: "#A1A1AA", fontFamily: "'Lora', serif" }}
          >
            Give investment advisors and operations teams more capacity with AI already operating in production.
          </p>
        </div>
      </section>

      {/* Layla's Core Platform Layers */}
<section
  className="pt-20 pb-20 md:pt-24 md:pb-24 px-6 md:px-12 lg:px-24"
  data-testid="platform-features"
  style={{
    background:
      "linear-gradient(180deg, #050508 0%, #2A2636 22%, #5B526A 55%, #3B3448 78%, #050508 100%)",
  }}
>
  <div className="max-w-5xl mx-auto">
    <div className="section-reveal mb-10">
      <h2
        className="text-2xl md:text-3xl font-medium mb-3"
        style={{ fontFamily: "'Lora', serif", color: "#F5F5F5" }}
      >
        Layla&apos;s Core Platform Layers
      </h2>

      <p
        className="text-xs md:text-sm max-w-2xl leading-relaxed"
        style={{ color: "#A1A1AA", fontFamily: "'Lora', serif" }}
      >
        Layla delivers modern advisory experiences and actionable intelligence so
        advisors can focus on what matters most: advising clients.
      </p>
    </div>

    {/* GRID CONTAINER (this is the one you want) */}
    <div className="grid grid-cols-2 gap-6 md:gap-8">
      <FeatureCard feature={platformFeatures[0]} index={0} />
      <FeatureCard feature={platformFeatures[1]} index={1} />

      <FeatureCard feature={platformFeatures[2]} index={2} />

      <FeatureCard feature={platformFeatures[3]} index={3} />
      <FeatureCard feature={platformFeatures[4]} index={4} />

      <FeatureCard feature={platformFeatures[5]} index={5} />
    </div>
  </div>
</section>

      {/* Save Hours Stats Section */}
      <StatsSection />

<section
  data-testid="final-cta"
  className="relative pt-28 md:pt-32 pb-46 md:pb-50 px-6 md:px-12 lg:px-24 overflow-hidden"
></section>

      <Footer />
    </div>
  );
}